/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import type { RefObject } from 'preact';
import type {
  DiagramContextMenuConfig,
  DiagramContextMenuContext,
  ItemInfo,
  PromotedLinkId
} from '../diagram.types';
import { type gestureTypes, useContextMenu } from '#hooks/PRIVATE_useVisContextMenu';
import { getDiagramContextMenuContext } from '../utils/contextMenuUtils';
import { getPromotedString } from '../utils/diagramUtils';

type Props<K1, K2, D1, D2> = {
  nodesMap: Map<K1, D1>;
  linksMap: Map<K2, D2>;
  promotedLinkMap: Map<string, PromotedLinkId<K1, D2>>;
  focusedItemInfo: ItemInfo<K1, K2, D2>;
  rootRef: RefObject<HTMLDivElement>;
  contextMenuConfig?: DiagramContextMenuConfig<K1, D1, D2>;
  onContextMenuDismissed?: (gesture: gestureTypes) => void;
};

export const useDiagramContextMenu = <K1, K2, D1, D2>({
  focusedItemInfo,
  rootRef,
  contextMenuConfig,
  onContextMenuDismissed,
  nodesMap,
  linksMap,
  promotedLinkMap
}: Props<K1, K2, D1, D2>) => {
  const { contextMenuProps, contextMenuContent } = useContextMenu<
    DiagramContextMenuContext<K1, D1, D2>
  >({
    onContextMenuHandler: ({ gesture, target }) => {
      let idx;
      let elementType: 'node' | 'link' | 'promotedLink' | 'background' = 'background';
      const currentTarget = target as HTMLElement;
      if (gesture === 'keyboard') {
        idx = focusedItemInfo.id;
        elementType = focusedItemInfo.type;
      } else if (
        (currentTarget.closest('[data-oj-private-node]') as HTMLElement)?.dataset['ojPrivateNode']
      ) {
        idx = (currentTarget.closest('[data-oj-private-node]') as HTMLElement)?.dataset[
          'ojPrivateNode'
        ] as K1;
        elementType = 'node';
      } else if (
        (currentTarget.closest('[data-oj-private-link]') as SVGElement)?.dataset[
          'ojPrivateLink'
        ] as K2
      ) {
        idx = (currentTarget.closest('[data-oj-private-link]') as SVGElement)?.dataset[
          'ojPrivateLink'
        ] as K2;
        elementType = 'link';
      } else if (
        (currentTarget.closest('[data-oj-private-promoted-link]') as SVGElement)?.dataset[
          'ojPrivatePromotedLink'
        ] as K2
      ) {
        idx = (currentTarget.closest('[data-oj-private-promoted-link]') as SVGElement)?.dataset[
          'ojPrivatePromotedLink'
        ] as string;
        idx = promotedLinkMap.get(idx);
        elementType = 'promotedLink';
      }

      const { context } = getDiagramContextMenuContext<K1, K2, D1, D2>(
        nodesMap,
        linksMap,
        elementType,
        idx
      );
      if (gesture === 'keyboard') {
        let element;
        if (focusedItemInfo.type === 'node') {
          element = rootRef.current?.querySelector(`[data-oj-private-node="${idx}"]`);
        } else if (focusedItemInfo.type === 'link') {
          element = rootRef.current?.querySelector(`[data-oj-private-link="${idx}"]`);
        } else {
          element = rootRef.current?.querySelector(
            `[data-oj-private-promoted-link="${getPromotedString(idx as PromotedLinkId<K1, D2>)}"]`
          );
        }
        return {
          type: 'element',
          context,
          elem: element ? element : null
        };
      }
      return { type: 'pointer', context };
    },
    contextMenuOptions: {
      isDisabled: !contextMenuConfig //If there is not a context menu renderer we disable the hook
    },
    rootRef,
    contextMenuConfig,
    onContextMenuDismissed
  });

  return { contextMenuContent, contextMenuProps };
};
